import { useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearchParams } from "wouter";
import { Bookmark, MapPin, PenLine, SlidersHorizontal } from "lucide-react";
import { MapView, type Camera, type DrawState, type MapPressPoint } from "../map/MapView";
import { gqlRequest } from "../lib/gql";
import { CREATE_SAVED_PLACE } from "../lib/queries";
import { useAuth } from "../lib/auth";
import { useViewportData, type Viewport } from "../map/useViewportData";
import { PINS_MIN_ZOOM, ROUTES_MIN_ZOOM } from "../map/zoomConfig";
import { loadCamera, restoreParams, saveCamera, saveFilters, urlHasCamera } from "../map/mapPrefs";
import { filtersFromParams, filtersToParams, countActiveFilters } from "../lib/filters";
import { useT, type MessageKey } from "../lib/i18n";
import { SearchBox } from "../components/SearchBox";
import { FilterSheet } from "../components/FilterSheet";
import { DrawRoutePanel } from "../components/DrawRoutePanel";
import { SavedPlacesMenu } from "../components/SavedPlacesMenu";
import { TextInput } from "../components/ui";
import { Button } from "@/components/ui/button";

// Default camera: the seeded Krkonoše area.
const DEFAULT_CAMERA: Camera = { lng: 6.87, lat: 45.92, zoom: 10 };

type LayerCount = { shown: number; total: number; truncated: boolean };

// Names the layer the zoom budget actually cut, so "showing 1500 of 13540
// routes" is never mistaken for the full picture. Reports the worst offender
// when several layers are capped at once.
const LAYER_NAME_KEYS: Record<string, MessageKey> = {
  places: "map.layerPlaces",
  routes: "map.layerRoutes",
  regions: "map.layerRegions",
  outings: "map.layerOutings",
  events: "map.layerEvents",
};

function truncationLabel(
  counts: Record<string, LayerCount>,
  t: (key: MessageKey, params?: Record<string, string | number>) => string,
  locale: string,
): string {
  const capped = Object.entries(counts).filter(([, c]) => c.truncated);
  const [name, c] = capped.reduce((worst, entry) =>
    entry[1].total - entry[1].shown > worst[1].total - worst[1].shown ? entry : worst,
  );
  const extra = capped.length > 1 ? t("map.truncatedMore", { count: capped.length - 1 }) : "";
  return (
    t("map.truncated", {
      shown: c.shown.toLocaleString(locale),
      total: c.total.toLocaleString(locale),
      layer: LAYER_NAME_KEYS[name] ? t(LAYER_NAME_KEYS[name]) : name,
    }) + extra
  );
}

export function MapPage() {
  const { t, locale } = useT();
  const [params, setParams] = useSearchParams();
  const [, navigate] = useLocation();
  const [viewport, setViewport] = useState<Viewport | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Draw-a-route mode (F43); non-null puts the main map in draw mode and
  // shows the route form as a popover next to the filter sheet.
  const [draw, setDraw] = useState<DrawState | null>(null);
  const [flyTo, setFlyTo] = useState<Camera | null>(null);
  // Right-click / long-press proposal for a new place (F41); null = closed.
  const [addPlaceAt, setAddPlaceAt] = useState<MapPressPoint | null>(null);
  // "Save this spot" bookmark form, opened from the same popover; null = closed.
  const [saveSpotAt, setSaveSpotAt] = useState<MapPressPoint | null>(null);
  const { personId } = useAuth();

  // Fold saved filters into the URL before the first render, so the initial
  // viewport query already carries them instead of firing twice. A link that
  // specifies its own filters restores nothing — restoreParams returns null.
  const restored = useRef(false);
  if (!restored.current) {
    restored.current = true;
    const next = restoreParams(params);
    if (next) setParams(next, { replace: true });
  }

  const filters = useMemo(() => filtersFromParams(params), [params]);

  // Camera is read exactly once: URL (shared link) → localStorage → default.
  const initialCamera = useRef<Camera>(
    urlHasCamera(params)
      ? {
          lng: Number(params.get("lng")) || DEFAULT_CAMERA.lng,
          lat: Number(params.get("lat")) || DEFAULT_CAMERA.lat,
          zoom: Number(params.get("z")) || DEFAULT_CAMERA.zoom,
        }
      : (loadCamera() ?? DEFAULT_CAMERA),
  );

  const data = useViewportData(viewport, filters);
  const activeCount = countActiveFilters(filters);

  const updateParams = (mutate: (p: URLSearchParams) => void) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      },
      { replace: true },
    );

  return (
    <div className="relative h-full w-full pb-16 sm:pb-0">
      <MapView
        initialCamera={initialCamera.current}
        onViewportChange={(bounds, zoom) => {
          setViewport({ ...bounds, zoom });
          // The popovers are anchored to a screen point; any camera move
          // would leave them floating over the wrong spot.
          setAddPlaceAt(null);
          setSaveSpotAt(null);
        }}
        onCameraChange={(camera) => {
          // Already debounced 300ms by MapView's moveend handler.
          saveCamera(camera);
          updateParams((p) => {
            p.set("lng", camera.lng.toFixed(4));
            p.set("lat", camera.lat.toFixed(4));
            p.set("z", camera.zoom.toFixed(1));
          });
        }}
        onContextMenu={(p) => {
          // While drawing, right-click shouldn't open the add-place proposal.
          if (!draw) setAddPlaceAt(p);
        }}
        draw={draw}
        onDrawChange={setDraw}
        places={data.places}
        routes={data.routes}
        regions={data.regions}
        outings={data.outings}
        events={data.events}
        clustering={filters.clustering}
        flyTo={flyTo}
      />

      {/* "Add a place here?" confirmation, anchored to the pressed point. The
          form page is LoginGate'd, so anonymous users get the login card there. */}
      {addPlaceAt && (
        <div
          className="absolute z-30 w-44 -translate-x-1/2 rounded-xl border border-border bg-background/95 p-3 shadow-pop backdrop-blur-sm"
          style={{
            left: Math.min(Math.max(addPlaceAt.x, 96), window.innerWidth - 96),
            top: Math.max(addPlaceAt.y - 12, 8),
            transform: "translate(-50%, -100%)",
          }}
        >
          <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            <MapPin aria-hidden className="size-4 shrink-0 text-primary" />
            {t("map.addPlaceHere")}
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => navigate(`/place/new?lng=${addPlaceAt.lng.toFixed(6)}&lat=${addPlaceAt.lat.toFixed(6)}`)}
              className="h-7 flex-1 text-xs font-semibold"
            >
              {t("map.add")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAddPlaceAt(null)}
              className="h-7 flex-1 text-xs font-semibold text-muted-foreground"
            >
              {t("common.cancel")}
            </Button>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(`/event/new?lng=${addPlaceAt.lng.toFixed(6)}&lat=${addPlaceAt.lat.toFixed(6)}`)}
            className="mt-2 h-7 w-full text-xs font-semibold text-muted-foreground"
          >
            {t("map.planEventHere")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (!personId) {
                navigate(`/login?next=${encodeURIComponent("/")}`);
                return;
              }
              setSaveSpotAt(addPlaceAt);
              setAddPlaceAt(null);
            }}
            className="mt-2 h-7 w-full text-xs font-semibold text-muted-foreground"
          >
            <Bookmark aria-hidden className="size-3.5" />
            {t("map.saveSpotHere")}
          </Button>
        </div>
      )}

      {saveSpotAt && personId && (
        <SaveSpotCard point={saveSpotAt} personId={personId} onClose={() => setSaveSpotAt(null)} />
      )}

      {/* Top overlay: search + filter button */}
      <div className="absolute inset-x-3 top-3 z-30 flex items-start gap-2">
        <SearchBox
          onFlyTo={setFlyTo}
          getCenter={() => ({
            lng: Number(params.get("lng")) || initialCamera.current.lng,
            lat: Number(params.get("lat")) || initialCamera.current.lat,
          })}
        />
        <button
          type="button"
          onClick={() => setSheetOpen((v) => !v)}
          aria-expanded={sheetOpen}
          className={`ml-auto flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium shadow-card transition-all duration-150 active:scale-[0.97] ${
            sheetOpen || activeCount > 0
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-border bg-background/95 text-foreground backdrop-blur-sm hover:border-ring/50 hover:shadow-card-hover"
          }`}
        >
          <SlidersHorizontal aria-hidden className="size-4" />
          <span className="hidden sm:inline">{t("map.filters")}</span>
          {activeCount > 0 && (
            <span className="rounded-full bg-primary px-1.5 text-xs font-semibold tabular-nums text-primary-foreground">
              {activeCount}
            </span>
          )}
        </button>
        <SavedPlacesMenu onFlyTo={setFlyTo} />
        {/* Draw-a-route entry (F43): toggles draw mode on this map — points
            are added right where the user is looking; the form is a popover. */}
        <button
          type="button"
          onClick={() => {
            setSheetOpen(false);
            setDraw((d) => (d ? null : { points: [], selected: null }));
          }}
          aria-expanded={draw != null}
          className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium shadow-card transition-all duration-150 active:scale-[0.97] ${
            draw
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-border bg-background/95 text-foreground backdrop-blur-sm hover:border-ring/50 hover:shadow-card-hover"
          }`}
        >
          <PenLine aria-hidden className="size-4" />
          <span className="hidden sm:inline">{t("map.drawRoute")}</span>
        </button>
      </div>

      {sheetOpen && (
        <FilterSheet
          filters={filters}
          onChange={(next) => {
            saveFilters(next);
            updateParams((p) => filtersToParams(next, p));
          }}
          onClose={() => setSheetOpen(false)}
        />
      )}

      {draw && <DrawRoutePanel draw={draw} onChange={setDraw} onClose={() => setDraw(null)} />}

      {/* Status pills */}
      <div className="pointer-events-none absolute bottom-20 left-1/2 z-20 -translate-x-1/2 sm:bottom-6">
        {viewport && viewport.zoom < PINS_MIN_ZOOM && (
          <span className="rounded-full bg-foreground/85 px-3.5 py-1.5 text-xs font-medium text-background shadow-panel backdrop-blur-sm">
            {t("map.zoomForData")}
          </span>
        )}
        {/* Between the pin floor and the route threshold, places/outings are
            drawn (clustered) but routes are not yet fetched. */}
        {!data.isFetching && filters.showRoutes && viewport && viewport.zoom >= PINS_MIN_ZOOM && viewport.zoom < ROUTES_MIN_ZOOM && (
          <span className="rounded-full bg-foreground/85 px-3.5 py-1.5 text-xs font-medium text-background shadow-panel backdrop-blur-sm">
            {t("map.zoomForRoutes")}
          </span>
        )}
        {data.isFetching && viewport && viewport.zoom >= PINS_MIN_ZOOM && (
          <span className="inline-flex items-center gap-2 rounded-full bg-foreground/70 px-3.5 py-1.5 text-xs font-medium text-background shadow-panel backdrop-blur-sm">
            <span className="size-3 animate-spin rounded-full border-2 border-background/30 border-t-background" />
            {t("common.loading")}
          </span>
        )}
        {!data.isFetching && data.truncated && (
          <span className="rounded-full bg-foreground/85 px-3.5 py-1.5 text-xs font-medium text-background shadow-panel backdrop-blur-sm">
            {truncationLabel(data.counts, t, locale)}
            {t("map.truncatedZoom")}
          </span>
        )}
        {data.error != null && (
          <span className="rounded-full bg-destructive/90 px-3.5 py-1.5 text-xs font-medium text-white shadow-panel backdrop-blur-sm">
            {t("map.loadError")}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Anchored "Save this spot" form: names a custom map position and stores it
 * as a saved_place row (geom + name, no placeId). Rendered only when logged
 * in — the popover button bounces anonymous users to /login instead.
 */
function SaveSpotCard({
  point,
  personId,
  onClose,
}: {
  point: MapPressPoint;
  personId: string;
  onClose: () => void;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");

  const save = useMutation({
    mutationFn: () =>
      gqlRequest(CREATE_SAVED_PLACE, {
        input: {
          // RLS insists this matches the JWT claim; nothing defaults it.
          personId,
          name: name.trim(),
          // GeoJSON object form — see the note on CREATE_PLACE in queries.ts.
          geom: { type: "Point", coordinates: [point.lng, point.lat] },
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved", personId] });
      onClose();
    },
  });

  return (
    <div
      className="absolute z-30 w-56 -translate-x-1/2 rounded-xl border border-border bg-background/95 p-3 shadow-pop backdrop-blur-sm"
      style={{
        left: Math.min(Math.max(point.x, 120), window.innerWidth - 120),
        top: Math.max(point.y - 12, 8),
        transform: "translate(-50%, -100%)",
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() && !save.isPending) save.mutate();
        }}
      >
        <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <Bookmark aria-hidden className="size-4 shrink-0 text-primary" />
          {t("map.saveSpotHere")}
        </p>
        <TextInput
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("map.spotNamePlaceholder")}
          className="mt-2 h-8 text-sm"
        />
        {save.error != null && (
          <p className="mt-1.5 text-xs text-destructive">
            {save.error instanceof Error ? save.error.message : String(save.error)}
          </p>
        )}
        <div className="mt-2 flex gap-2">
          <Button
            type="submit"
            size="sm"
            disabled={!name.trim() || save.isPending}
            className="h-7 flex-1 text-xs font-semibold"
          >
            {t("common.save")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            className="h-7 flex-1 text-xs font-semibold text-muted-foreground"
          >
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </div>
  );
}
